import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import {
  FeatureName,
  WorkspaceAsset,
  WorkspaceRoutine,
  useFilteredWorkspaceRoutines,
  useIsFeatureEnabled,
  useUpdateMutation
} from '@gripp/shared-logic';
import { BottomComponentBar, Button, Icon, useSystemNotification } from '@gripp/shared-ui';
import { extractAndDeduplicateRoutines } from 'libs/shared-ui/src/utils/assetHelpers';
import _ from 'lodash';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle
} from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Text } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import Sortable, { SortableGridDragEndParams } from 'react-native-sortables';
import { Spacing } from '../../../styles';
import { Colors } from '../../../themes';
import { EditRoutineModalOld } from './editRoutineModal-old';
import { RequestRoutineModal } from './requestRoutine';

type EditRoutineModalProps = {
  workspaceAsset: WorkspaceAsset;
  editRoutineModalVisible: boolean;
  onClose: () => void;
  modalStyle?: StyleProp<ViewStyle>;
  isWeb?: boolean;
};

export const EditRoutineModal: React.FC<EditRoutineModalProps> = (props) => {
  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  if (!isCropCircleRedesignEnabled) {
    return <EditRoutineModalOld {...props} />;  
  }

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  return (
    <EditRoutineModalBase {...props} />
  );
};

export const EditRoutineModalBase: React.FC<EditRoutineModalProps> = ({
  editRoutineModalVisible,
  onClose,
  workspaceAsset,
  modalStyle,
  isWeb,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSystemNotification();
  const [selectedRoutines, setSelectedRoutines] = useState<RoutineButtonData[]>([]);
  const initialSelectedRoutines = useRef<RoutineButtonData[] | undefined>(undefined);
  const saveAbortControllerRef = useRef<AbortController | null>(null);
  const [isDraggingRoutine, setIsDraggingRoutine] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { mutateAsync: updateWorkspaceAsset } = useUpdateMutation({
    modelName: 'WorkspaceAsset',
    query: UPDATE_WORKSPACE_ASSET,
  });

  const routineData = useFilteredWorkspaceRoutines(
    {
      workspace: { eq: workspaceAsset.workspace.id },
      deactivated: { ne: true },
    },
    workspaceAsset.workspace.id,
    !workspaceAsset
  );

  const assetRoutines: string[] = useMemo(() => {
    if (!workspaceAsset.routines || workspaceAsset.routines.length === 0) {
      return [];
    }

    return workspaceAsset.routines.map((routine) => routine.id);
  }, [workspaceAsset]);

  const externalRoutines = useMemo(() => {
    if (
      !workspaceAsset.asset.product?.routines &&
      !workspaceAsset.groups
    ) {
      return [];
    }

    const allExternalRoutines: WorkspaceRoutine[] = extractAndDeduplicateRoutines(workspaceAsset);

    return allExternalRoutines
      .sort((a, b) => {
        return a.order - b.order;
      })
      .map((routine) => createRoutineButtonData(routine, assetRoutines));
  }, [
    workspaceAsset,
    assetRoutines
  ]);

  const workspaceRoutines = useMemo(() => {
    if (!routineData || routineData.length === 0) {
      return [];
    }

    const eRoutines = externalRoutines || []; 

    return routineData
      .filter((r) => {
        return !r.deactivated &&
         !eRoutines.some(pRoutine => pRoutine.routineId === r.id);
      })
      .map((routine) => createRoutineButtonData(routine, assetRoutines));
  }, [routineData, externalRoutines, assetRoutines]);

  useEffect(() => {
    const selectedRoutines = workspaceRoutines
      .filter((routine) => routine.isAssetRoutine === true)
      .sort((a, b) => {
        let routineA, routineB;

        if (
          workspaceAsset.routineOrder &&
          workspaceAsset.routineOrder.length > 0
        ) {
          routineA = workspaceAsset.routineOrder.find(
            (x) => x.workspaceRoutineId === a.routineId
          );
          routineB = workspaceAsset.routineOrder.find(
            (x) => x.workspaceRoutineId === b.routineId
          );
        }
        const aOrder = routineA?.order ?? a.order;
        const bOrder = routineB?.order ?? b.order;
        return aOrder - bOrder;
      });

    if (workspaceRoutines.length && !initialSelectedRoutines.current) {
      initialSelectedRoutines.current = selectedRoutines;
    }

    setSelectedRoutines(selectedRoutines);
  }, [workspaceRoutines, workspaceAsset.routineOrder, initialSelectedRoutines]);

  // Cleanup effect to cancel pending operations on unmount
  useEffect(() => {
    return () => {
      // Cancel any pending save operation
      if (saveAbortControllerRef.current) {
        saveAbortControllerRef.current.abort();
      }
    };
  }, []);

  const onCancel = () => {
    onClose();
  };

  const saveRoutines = useCallback(async (routineData: RoutineButtonData[], showSuccessMessage = true) => {
    setHasChanges(true);

    // Cancel any existing save operation
    if (saveAbortControllerRef.current) {
      saveAbortControllerRef.current.abort();
    }

    // Create new abort controller for this save operation
    const abortController = new AbortController();
    saveAbortControllerRef.current = abortController;

    try {
      const routineOrder = routineData.map((routine, i) => {
        return { order: i, workspaceRoutineId: routine.routineId };
      });

      const routines = routineData.map((routine) => {
        return { id: routine.routineId };
      });

      const input = {
        id: workspaceAsset.id,
        routineOrder: routineOrder,
        routines: routines,
      };

      // Check if operation was aborted before making the request
      if (abortController.signal.aborted) {
        return false;
      }

      await updateWorkspaceAsset({ input });
      
      // Check if operation was aborted after the request
      if (abortController.signal.aborted) {
        return false;
      }
      
      if (showSuccessMessage) {
        showSuccess(t('routines.editRoutines.saveSuccess'));
      }
      
      return true;
    } catch (error) {
      // Don't show error if operation was aborted
      if (!abortController.signal.aborted) {
        showError(t('routines.editRoutines.saveError'));
      }
      return false;
    } finally {
      // Clear the abort controller if this was the current one
      if (saveAbortControllerRef.current === abortController) {
        saveAbortControllerRef.current = null;
      }
    }
  }, [updateWorkspaceAsset, workspaceAsset.id, showSuccess, showError, t]);

  const onReset = useCallback(() => {
    if (initialSelectedRoutines.current) {
      const nextSelectedRoutines = initialSelectedRoutines.current;
      setSelectedRoutines(nextSelectedRoutines);
      saveRoutines(nextSelectedRoutines, false);
    }
  }, [saveRoutines]);

  const BottomBar = useMemo(() => {
    const backButton = (
      <Button
        variant='rounded'
        onPress={onClose}
        leftIcon={<Icon name="Chevron Left" size={24} color={Colors.white} />}
        state="active"
        style={styles.backButton}
      />
    );

    const requestRoutineButton = (
      <RequestRoutineModal
        assetId={workspaceAsset.asset.id}
        workspaceId={workspaceAsset.workspace.id}
        isWeb={isWeb}
      />
    );
    
    return (
      <BottomComponentBar
        backButton={backButton}
        additionalHeaderBlock={requestRoutineButton}
        onRightPress={onReset}
        rightPressDisabled={!hasChanges}
      />
    );
  }, [
    onClose, 
    workspaceAsset.asset.id, 
    workspaceAsset.workspace.id, 
    isWeb, 
    onReset, 
    hasChanges
  ]);

  const modalChildren = () => (
    <SafeAreaView style={styles.contentContainer}>
      <ScrollView
        scrollEnabled={!isDraggingRoutine}
      >
        <Text style={styles.routineDescription}>
          {t('routines.editRoutines.routineInWorkplace')}
        </Text>
        <RoutineSubButtons
          uneditableButtons={externalRoutines}
          routineButtons={workspaceRoutines}
          selectedRoutines={selectedRoutines}
          setSelectedRoutines={setSelectedRoutines}
          onSaveRoutines={saveRoutines}
          showSuccess={showSuccess}
          onDragStateChange={setIsDraggingRoutine}
        />
      </ScrollView>
    </SafeAreaView>
  );

  return isWeb ? (
    <Modal
      visible={editRoutineModalVisible}
      animationType="fade"
      transparent={true}
      onRequestClose={onCancel}
    >
      <View style={styles.webModalBackground}>
        <View style={styles.modalContainer}>{modalChildren()}</View>
      </View>
      {BottomBar}
    </Modal>
  ) : (
    <Modal
      visible={editRoutineModalVisible}
      presentationStyle={'pageSheet'}
      animationType={'slide'}
      onRequestClose={onCancel}
      style={modalStyle ?? styles.modalContainer}
    >
      {modalChildren()}
      {BottomBar}
    </Modal>
  );
};

// Factory to create RoutineButtonData from routine object
const createRoutineButtonData = (
  routine: WorkspaceRoutine, 
  assetRoutines?: string[]
): RoutineButtonData => {
  return {
    routineButtonText: routine.config.routineButtonText || routine.config.title || '',
    title: routine.config.title,
    routineId: routine.id,
    order: routine.order,
    ...(assetRoutines && { isAssetRoutine: assetRoutines.includes(routine.id) }),
  };
};

export type RoutineButtonData = {
  routineId: string;
  routineButtonText: string;
  title: string;
  order: number;
  isAssetRoutine?: boolean;
  isProductRoutine?: boolean;
};

type RoutineButtonsProps = {
  uneditableButtons: RoutineButtonData[];
  routineButtons: RoutineButtonData[];
  selectedRoutines: RoutineButtonData[];
  setSelectedRoutines: React.Dispatch<React.SetStateAction<RoutineButtonData[]>>;
  onSaveRoutines: (routineData: RoutineButtonData[], showSuccessMessage?: boolean) => Promise<boolean>;
  showSuccess: (message: string) => void;
  onDragStateChange: (isDragging: boolean) => void;
};

export const RoutineSubButtons: React.FC<RoutineButtonsProps> = (props) => {
  const {routineButtons, uneditableButtons, selectedRoutines, setSelectedRoutines, onSaveRoutines, showSuccess, onDragStateChange } = props;
  const { t } = useTranslation();

  const unselectedRoutines = useMemo(
    () => _.differenceBy(routineButtons, selectedRoutines, 'routineId'),
    [selectedRoutines, routineButtons]
  );

  const onRemoveRoutine = useCallback((routine: RoutineButtonData, index: number) => {
    // Get updated routines after removal
    const currentRoutines = selectedRoutines as RoutineButtonData[];
    const updatedRoutines = currentRoutines.filter((_, i) => i !== index);
    
    // Update local state immediately for UI responsiveness
    setSelectedRoutines(updatedRoutines);
    
    // Save with debounce
    onSaveRoutines(updatedRoutines, false);
    
    // Show success message immediately
    showSuccess(t('routines.editRoutines.routineRemoved', { title: routine.title }));
  }, [selectedRoutines, setSelectedRoutines, onSaveRoutines, showSuccess, t]);

  const onAppendRoutine = useCallback((routine: RoutineButtonData, index: number) => {
    const currentRoutines = selectedRoutines as RoutineButtonData[];
    const updatedRoutines = [...currentRoutines, routine];
    
    setSelectedRoutines(updatedRoutines);
    
    // Save with debounce
    onSaveRoutines(updatedRoutines, false);
    
    // Show success message immediately
    showSuccess(t('routines.editRoutines.routineAdded', { title: routine.title }));
  }, [selectedRoutines, setSelectedRoutines, onSaveRoutines, showSuccess, t]);

  const onMoveRoutine = useCallback((e: SortableGridDragEndParams<RoutineButtonData>) => {
    onDragStateChange(false);

    const { fromIndex, toIndex } = e;

    if (fromIndex !== toIndex) {
      const currentRoutines = [...selectedRoutines];
      const [movedItem] = currentRoutines.splice(fromIndex, 1);
      currentRoutines.splice(toIndex, 0, movedItem);
      
      // Update local state immediately for UI responsiveness
      setSelectedRoutines(currentRoutines);
      
      // Save with debounce
      onSaveRoutines(currentRoutines, false);
      
      // Show success message immediately
      showSuccess(t('routines.editRoutines.routineReordered'));
    }
  }, [selectedRoutines, t, setSelectedRoutines, onSaveRoutines, showSuccess, onDragStateChange]);
  
  const renderItem = ({ item: routine, index }: { item: RoutineButtonData; index: number }) => {
    return (
      <AssignRoutineButton
        key={`selected-routine-${routine.routineId}`}
        routine={routine}
        buttonStyle={styles.selectedRoutineButton}
      >
        <TouchableOpacity 
          style={styles.checkbox}
          onPress={() => onRemoveRoutine(routine, index)}
          activeOpacity={0.7}
        >
          <MaterialCommunityIcons
            name="checkbox-marked"
            size={24}
            color={Colors.grayText}
          />
        </TouchableOpacity>
      </AssignRoutineButton>
    );
  };

  return (
    <View style={{ marginBottom: 12 }}>
      {
        uneditableButtons && uneditableButtons.length > 0 && uneditableButtons.map((routine, idx) => (
          <AssignRoutineButton
            key={`product-routine-${idx}`}
            routine={routine}
            buttonStyle={{
              ...styles.unselectedRoutineButton,
              backgroundColor: Colors.graySpacer,
            }}
          />
        ))
      }
      {/* NOTE: Important to have GestureHandlerRootView for Android drag and drop on Modal */}
      <GestureHandlerRootView>
        <Sortable.Grid
          columnGap={10}
          columns={1}
          data={selectedRoutines}
          dropIndicatorStyle={styles.dropIndicator}
          renderItem={renderItem}
          rowGap={0}
          showDropIndicator
          overDrag={'none'}
          activeItemScale={0.99}
          keyExtractor={(item) => item.routineId}
          onDragStart={() => onDragStateChange(true)}
          onDragEnd={onMoveRoutine}
        />
      </GestureHandlerRootView>
      {unselectedRoutines?.map((routine, index) => {
        return (
          <AssignRoutineButton
            key={`unselected-routine-${routine.routineId}`}
            routine={routine}
            buttonStyle={styles.unselectedRoutineButton}
          >
            <MaterialCommunityIcons
              name="checkbox-blank-outline"
              size={24}
              onPress={() => onAppendRoutine(routine, index)}
              color={Colors.grayText}
              style={styles.checkbox}
            />
          </AssignRoutineButton>
        );
      })}
    </View>
  );
};

type AssignRoutineButtonProps = {
  routine: RoutineButtonData;
  onClick?: () => void;
  buttonStyle?: StyleProp<ViewStyle>;
  leftButtonIcon?: ReactNode;
  children?: ReactNode;
};

const AssignRoutineButton = (props: AssignRoutineButtonProps) => {
  const { t } = useTranslation();

  return (
    <View style={[styles.assignButton, props.buttonStyle]}>
      <TouchableOpacity
        onPress={props.onClick}
        style={styles.routineContent}
        activeOpacity={0.7}
      >
        <View style={styles.labelContainer}>
          <Text style={styles.assignButtonTitle}>{props.routine.title}</Text>
          <Text style={styles.assignButtonLabel}>
            {t('routines.editRoutines.label')} {props.routine.routineButtonText}
          </Text>
        </View>
      </TouchableOpacity>
      <View style={styles.checkboxContainer}>
        {props.children}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  webModalBackground: {
    backgroundColor: Colors.blackInactive,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    ...Platform.select({
      web: {
        width: 560,
        height: 650,
        borderRadius: 12,
        backgroundColor: Colors.primaryGrayBackground,
        overflow: 'hidden',
      },
      default: {
        flex: 1,
        height: '100%',
        textAlign: 'center',
        borderRadius: 0,
      },
    }),
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
    marginBottom: 0,
    paddingBottom: 60,
    backgroundColor: Colors.white,
  },
  routineDescription: {
    paddingBottom: Spacing.basePadding.base / 2,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 16,
  },
  selectedRoutineButton: {
    color: Colors.grayText,
    backgroundColor: Colors.blueLight,
    borderColor: Colors.blueLight,
    borderRadius: 0
  },
  unselectedRoutineButton: {
    color: Colors.grayText,
    backgroundColor: Colors.primaryGrayBackground,
    borderColor: Colors.primaryGrayBackground,
    borderRadius: 0
  },
  labelContainer: {
    minHeight: 36,
    flexDirection: 'column',
    justifyContent: 'center',
    flexShrink: 1,
    overflow: 'hidden',
  },
  assignButton: {
    backgroundColor: Colors.white,
    borderRadius: 8,
    borderColor: Colors.graySpacer,
    borderWidth: 1,
    minHeight: 64,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginVertical: 4,
  },
  routineContent: {
    flex: 1,
    paddingRight: 12,
  },
  assignButtonTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.black,
    marginBottom: 2,
  },
  assignButtonLabel: {
    fontSize: 14,
    fontWeight: '400',
    color: Colors.grayText,
  },
  checkboxContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 32,
  },
  dropIndicator: {
    backgroundColor: Colors.graySpacer,
    borderWidth: 0,
    borderRadius: 0,
  },
  checkbox: {
    width: 24,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
  },
  bottomBarTitleContainer: {
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 999,
    height: 40,
    paddingHorizontal: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bottomBarTitleText: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.black,
  },
  backButton: {
    width: 56,
    height: 56,
    minWidth: 56,
    maxHeight: 56,
    borderRadius: 99,
    marginLeft: 16,
  },
});

const UPDATE_WORKSPACE_ASSET = `
  mutation Mutation($input: MutateWorkspaceAssetInput!) {
  updateWorkspaceAsset(input: $input) {
    id
  }
}
`;
